import { useEffect, useRef, useState } from 'react';
import shipped, { usableManifest } from '../../utils/camera/detect/manifest';
import { createDetector, detectorSupported } from '../../utils/camera/detect/detectorClient';

/*
 * The on-device detector as a hook. `state` is:
 *   absent     the app ships no model (deviceDetector.json), which is today. Nothing is fetched.
 *   off        there's a model, and nobody asked for it.
 *   loading, running, too_slow, failed (with `reason`).
 * Two frames a second at most: the budget is per run, and a battery lasts longer than a benchmark.
 */
export const DETECT_MS = 500;
const EMPTY_SUMMARY = Object.freeze({ runs: 0, kept: 0, skipped: 0, p50: null, p95: null });

export function useDeviceDetector(videoRef, on, {
  manifest = shipped, make = createDetector, supported = detectorSupported, intervalMs = DETECT_MS,
} = {}) {
  const present = usableManifest(manifest);
  const [status, setStatus] = useState({ state: 'off' });
  const [boxes, setBoxes] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const madeRef = useRef(make);
  madeRef.current = make;
  const supportedRef = useRef(supported);
  supportedRef.current = supported;

  useEffect(() => {
    if (!present || !on) {
      setStatus({ state: 'off' });
      setBoxes([]);
      return undefined;
    }
    if (!supportedRef.current()) {
      setStatus({ state: 'failed', reason: 'not_supported' });
      return undefined;
    }
    setSummary(EMPTY_SUMMARY);
    const detector = madeRef.current({
      manifest,
      onState: setStatus,
      onBoxes: (found, latest) => {
        if (found) setBoxes(found);
        setSummary(latest);
      },
    });
    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      detector.send(videoRef.current);
    }, intervalMs);
    return () => {
      clearInterval(timer);
      detector.close();
    };
  }, [present, on, manifest, intervalMs, videoRef]);

  return { ...status, state: present ? status.state : 'absent', boxes, summary };
}
